import { WIDGET_CSS, HOST_CSS } from "./styles.js";
import { TOOLS, PROFILES, setFontScale, type Tool, type Profile } from "./tools.js";
import { loadPrefs, savePrefs, resetPrefs, type Prefs } from "./state.js";

type Config = {
  siteId: string;
  accent?: string;
};

type BarrierState = "idle" | "open" | "submitting" | "done" | "error";

const ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="8" r="1.5" fill="currentColor"/><path d="M12 11v9M8 14l4-2 4 2"/></svg>`;
const CLOSE_ICON = `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`;

const CONFIG_ENDPOINT = "https://uniqu-widget-config.everyai-com.workers.dev";

// Capture synchronously at script parse time — document.currentScript is
// null inside callbacks and async functions in IIFE bundles.
const _scriptEl = document.currentScript as HTMLScriptElement | null;

const BARRIER_ORIGIN = (() => {
  try {
    return _scriptEl?.src ? new URL(_scriptEl.src).origin : "";
  } catch {
    return "";
  }
})();

let barrierState: BarrierState = "idle";

async function fetchRemoteConfig(siteId: string): Promise<{ accentColor?: string; position?: string } | null> {
  // 1-attempt, 2-second fetch — if the worker is slow, we just use defaults
  // and let the widget render without waiting. Customer settings catch
  // up on the next page load.
  try {
    const ctrl = new AbortController();
    const to = setTimeout(() => ctrl.abort(), 2000);
    const res = await fetch(`${CONFIG_ENDPOINT}/c/${encodeURIComponent(siteId)}`, {
      signal: ctrl.signal,
      cache: "default",
    }).finally(() => clearTimeout(to));
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

async function init() {
  const siteId = _scriptEl?.getAttribute("data-site") ?? "demo";
  const accentOverride = _scriptEl?.getAttribute("data-accent");

  // Inject host-level CSS once, outside Shadow DOM, so it can affect the page.
  if (!document.getElementById("uq-host-styles")) {
    const hostStyle = document.createElement("style");
    hostStyle.id = "uq-host-styles";
    hostStyle.textContent = HOST_CSS;
    document.head.appendChild(hostStyle);
  }

  // Mount point + Shadow DOM keeps our internal UI isolated from the page.
  const mount = document.createElement("div");
  mount.id = "uniqu-widget-root";
  mount.style.cssText = "position: relative; z-index: 2147483647;";
  document.body.appendChild(mount);
  const shadow = mount.attachShadow({ mode: "open" });

  const style = document.createElement("style");
  style.textContent = WIDGET_CSS;
  shadow.appendChild(style);

  const root = document.createElement("div");
  shadow.appendChild(root);

  // Fetch remote config in parallel with rendering — show defaults
  // immediately, then re-render once config arrives. UX feels instant.
  const remote = siteId !== "demo" ? await fetchRemoteConfig(siteId) : null;
  const accent = accentOverride ?? remote?.accentColor ?? "#4f46e5";
  const position = remote?.position ?? "bottom-right";

  root.style.setProperty("--uq-accent", accent);
  root.setAttribute("data-uq-position", position);

  const prefs = loadPrefs();
  // First visit: honour the visitor's OS accessibility settings so the page
  // matches their stated intent without them touching anything.
  if (!prefs.osApplied) {
    try {
      if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
        prefs.active["stop-animations"] = true;
      }
    } catch {
      /* matchMedia may be unavailable */
    }
    prefs.osApplied = true;
    savePrefs(prefs);
  }

  render(root, { siteId, accent }, prefs);
  applyPrefs(prefs);
}

// Renders just the barrier section without touching the rest of the widget.
// This keeps the panel open and preserves focus during form interaction.
function renderBarrierSection(section: HTMLElement, root: HTMLElement, config: Config) {
  if (barrierState === "idle") {
    section.innerHTML = `<button class="uq-barrier-btn" aria-expanded="false">Report accessibility barrier</button>`;
    section.querySelector(".uq-barrier-btn")!.addEventListener("click", () => {
      barrierState = "open";
      renderBarrierSection(section, root, config);
      section.querySelector<HTMLElement>(".uq-barrier-desc")?.focus();
    });
    return;
  }

  if (barrierState === "done") {
    section.innerHTML = `<div class="uq-barrier-msg ok">✓ Report received. Thank you!</div>`;
    return;
  }

  const isSubmitting = barrierState === "submitting";
  const isError = barrierState === "error";

  section.innerHTML = `
    <p class="uq-barrier-title">What's making this page hard to use?</p>
    ${isError ? `<p class="uq-barrier-msg err">Something went wrong — please try again.</p>` : ""}
    <textarea class="uq-barrier-desc" placeholder="Describe the issue…" rows="3" maxlength="2000"
      aria-label="Describe the accessibility issue"${isSubmitting ? " disabled" : ""}></textarea>
    <input class="uq-barrier-email" type="email" placeholder="Email (optional, for follow-up)"
      maxlength="254" aria-label="Your email address"${isSubmitting ? " disabled" : ""} />
    <div class="uq-barrier-actions">
      <button class="uq-barrier-cancel"${isSubmitting ? " disabled" : ""}>Cancel</button>
      <button class="uq-barrier-submit"${isSubmitting ? " disabled" : ""}>${isSubmitting ? "Sending…" : "Send report"}</button>
    </div>
  `;

  section.querySelector(".uq-barrier-cancel")?.addEventListener("click", () => {
    barrierState = "idle";
    renderBarrierSection(section, root, config);
  });

  section.querySelector(".uq-barrier-submit")?.addEventListener("click", async () => {
    const desc = (section.querySelector(".uq-barrier-desc") as HTMLTextAreaElement)?.value.trim();
    const email = (section.querySelector(".uq-barrier-email") as HTMLInputElement)?.value.trim();
    if (!desc) {
      section.querySelector<HTMLElement>(".uq-barrier-desc")?.focus();
      return;
    }
    barrierState = "submitting";
    renderBarrierSection(section, root, config);

    try {
      const endpoint = BARRIER_ORIGIN ? `${BARRIER_ORIGIN}/api/barrier` : "/api/barrier";
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          siteId: config.siteId,
          url: window.location.href,
          description: desc,
          email: email || undefined,
        }),
      });
      barrierState = res.ok ? "done" : "error";
    } catch {
      barrierState = "error";
    }

    // Re-query the section — the widget may have re-rendered during the fetch
    const live = root.querySelector(".uq-barrier-section") as HTMLElement | null;
    if (live) renderBarrierSection(live, root, config);
  });
}

/** Focusable elements inside the panel, for the focus trap. */
function focusable(panel: HTMLElement): HTMLElement[] {
  return Array.from(
    panel.querySelectorAll<HTMLElement>(
      'button:not([disabled]), a[href], textarea:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ).filter((el) => el.offsetParent !== null || el === panel.querySelector(":focus"));
}

function render(root: HTMLElement, config: Config, prefs: Prefs) {
  const profileChips = PROFILES.map(
    (p) =>
      `<button class="uq-profile" data-profile="${p.id}" aria-pressed="${prefs.profile === p.id}">${p.label}</button>`,
  ).join("");

  root.innerHTML = `
    <button class="uq-toggle" aria-label="Open accessibility menu" aria-expanded="false" aria-haspopup="dialog">${ICON}</button>
    <div class="uq-panel" role="dialog" aria-modal="true" aria-label="Accessibility options">
      <div class="uq-header">
        <h2>Accessibility</h2>
        <button class="uq-close" aria-label="Close accessibility menu">${CLOSE_ICON}</button>
      </div>
      <div class="uq-body">
        <div class="uq-section-label">Profiles</div>
        <div class="uq-profiles" role="group" aria-label="Accessibility profiles">${profileChips}</div>
        <div class="uq-section-label">Adjustments</div>
        <div class="uq-row" aria-label="Text size">
          <button data-action="font-down" aria-label="Decrease text size">A−</button>
          <span class="uq-font-display" aria-live="polite">${Math.round(prefs.fontScale * 100)}%</span>
          <button data-action="font-up" aria-label="Increase text size">A+</button>
        </div>
        <div class="uq-tools-list"></div>
        <div class="uq-barrier-section uq-barrier"></div>
      </div>
      <div class="uq-footer">
        <button class="uq-reset">Reset all</button>
        <a href="https://uniqu-marketing.pages.dev/?utm_source=widget&amp;utm_medium=referral#scan" target="_blank" rel="noopener">Powered by uniqu</a>
      </div>
    </div>
  `;

  const toggle = root.querySelector(".uq-toggle") as HTMLButtonElement;
  const panel = root.querySelector(".uq-panel") as HTMLElement;
  const close = root.querySelector(".uq-close") as HTMLButtonElement;
  const list = root.querySelector(".uq-tools-list") as HTMLElement;
  const fontDisplay = root.querySelector(".uq-font-display") as HTMLElement;
  const reset = root.querySelector(".uq-reset") as HTMLButtonElement;
  const barrierSection = root.querySelector(".uq-barrier-section") as HTMLElement;

  // Refresh every control's visual state from prefs — no full re-render, so
  // the panel stays open and focus is preserved while toggling.
  const refreshUI = () => {
    list.querySelectorAll<HTMLElement>(".uq-tool").forEach((btn) => {
      const id = btn.dataset.toolId!;
      const on = !!prefs.active[id];
      btn.setAttribute("aria-pressed", String(on));
      const state = btn.querySelector(".uq-tool-state");
      if (state) state.textContent = on ? "On" : "Off";
    });
    root.querySelectorAll<HTMLElement>(".uq-profile").forEach((btn) => {
      btn.setAttribute("aria-pressed", String(prefs.profile === btn.dataset.profile));
    });
    fontDisplay.textContent = `${Math.round(prefs.fontScale * 100)}%`;
  };

  const openPanel = () => {
    panel.classList.add("uq-open");
    toggle.setAttribute("aria-expanded", "true");
    focusable(panel)[0]?.focus();
  };
  const closePanel = () => {
    panel.classList.remove("uq-open");
    toggle.setAttribute("aria-expanded", "false");
    toggle.focus();
  };

  toggle.addEventListener("click", () => {
    if (panel.classList.contains("uq-open")) closePanel();
    else openPanel();
  });
  close.addEventListener("click", closePanel);

  // Keyboard: Esc closes, Tab is trapped inside the open dialog.
  root.addEventListener("keydown", (e) => {
    if (!panel.classList.contains("uq-open")) return;
    if (e.key === "Escape") {
      e.preventDefault();
      closePanel();
      return;
    }
    if (e.key === "Tab") {
      const items = focusable(panel);
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = (root.getRootNode() as ShadowRoot).activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });

  // Tool toggles — update in place.
  for (const tool of TOOLS) {
    const btn = document.createElement("button");
    btn.className = "uq-tool";
    btn.dataset.toolId = tool.id;
    btn.setAttribute("aria-pressed", String(!!prefs.active[tool.id]));
    btn.innerHTML = `
      <span class="uq-tool-label">${tool.label}</span>
      <span class="uq-tool-state">${prefs.active[tool.id] ? "On" : "Off"}</span>
    `;
    btn.addEventListener("click", () => {
      const willBeOn = !prefs.active[tool.id];
      if (willBeOn && tool.group) {
        for (const other of TOOLS) {
          if (other.group === tool.group && other.id !== tool.id && prefs.active[other.id]) {
            setTool(other, false);
            prefs.active[other.id] = false;
          }
        }
      }
      setTool(tool, willBeOn);
      prefs.active[tool.id] = willBeOn;
      prefs.profile = undefined; // manual change leaves any preset
      savePrefs(prefs);
      refreshUI();
    });
    list.appendChild(btn);
  }

  // Profile presets.
  root.querySelectorAll<HTMLElement>(".uq-profile").forEach((btn) => {
    const profile = PROFILES.find((p) => p.id === btn.dataset.profile)!;
    btn.addEventListener("click", () => {
      if (prefs.profile === profile.id) clearAll(prefs);
      else applyProfile(profile, prefs);
      savePrefs(prefs);
      refreshUI();
    });
  });

  // Font size buttons.
  root.querySelector('[data-action="font-up"]')?.addEventListener("click", () => {
    prefs.fontScale = Math.min(2, +(prefs.fontScale + 0.1).toFixed(2));
    setFontScale(prefs.fontScale);
    fontDisplay.textContent = `${Math.round(prefs.fontScale * 100)}%`;
    savePrefs(prefs);
  });
  root.querySelector('[data-action="font-down"]')?.addEventListener("click", () => {
    prefs.fontScale = Math.max(0.8, +(prefs.fontScale - 0.1).toFixed(2));
    setFontScale(prefs.fontScale);
    fontDisplay.textContent = `${Math.round(prefs.fontScale * 100)}%`;
    savePrefs(prefs);
  });

  reset.addEventListener("click", () => {
    clearAll(prefs);
    barrierState = "idle";
    resetPrefs();
    refreshUI();
    renderBarrierSection(barrierSection, root, config);
  });

  // Populate barrier section with current state
  renderBarrierSection(barrierSection, root, config);
}

/** Turn off every active tool and reset font + profile. */
function clearAll(prefs: Prefs) {
  for (const tool of TOOLS) {
    if (prefs.active[tool.id]) setTool(tool, false);
  }
  prefs.active = {};
  prefs.fontScale = 1;
  prefs.profile = undefined;
  setFontScale(1);
}

/** Apply a curated profile: clear current state, then enable its tools. */
function applyProfile(profile: Profile, prefs: Prefs) {
  clearAll(prefs);
  for (const id of profile.tools) {
    const tool = TOOLS.find((t) => t.id === id);
    if (!tool) continue;
    setTool(tool, true);
    prefs.active[id] = true;
  }
  if (profile.fontScale) {
    prefs.fontScale = profile.fontScale;
    setFontScale(profile.fontScale);
  }
  prefs.profile = profile.id;
}

function setTool(tool: Tool, on: boolean) {
  if (tool.hostClass) {
    document.documentElement.classList.toggle(tool.hostClass, on);
  }
  if (on && tool.onEnable) tool.onEnable();
  if (!on && tool.onDisable) tool.onDisable();
}

function applyPrefs(prefs: Prefs) {
  for (const tool of TOOLS) {
    if (prefs.active[tool.id]) setTool(tool, true);
  }
  if (prefs.fontScale !== 1) setFontScale(prefs.fontScale);
}

// Boot — wait for DOM ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
